import { inject, Injectable } from '@angular/core';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { getCollections, TenantConfig } from '@models';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { TenantContextService } from './tenant-context.service';
import { SportContextService } from './sport-context.service';

/**
 * FederationContextService
 *
 * Provides the TenantConfig and collection paths of the federation that the
 * active club tenant belongs to. Active only when:
 *   1. The current tenant is `type === 'club'`
 *   2. The user's JWT claims contain `federationId`
 *
 * This service enables club managers to access federation data (seasons,
 * matches, rankings) without any additional login steps. The federation
 * context is loaded automatically after login and is transparent to the user.
 *
 * Order-of-registration transparency:
 *   - If the club onboards before the federation links to it, `federationId`
 *     will be absent from claims. Once super-admin runs `linkClubToFederation`,
 *     the Cloud Function refreshes claims for all managers. On the next token
 *     refresh (automatic or forced via `AuthService.refreshToken()`), this
 *     service starts emitting.
 *   - If the federation links the club before the manager first logs in,
 *     `buildMergedClaims` already embeds `federationId` at login time.
 */
@Injectable({ providedIn: 'root' })
export class FederationContextService {
  private readonly tenantContext = inject(TenantContextService);
  private readonly firestore = inject(Firestore);
  private readonly sportContext = inject(SportContextService);

  private federationConfigSubject = new BehaviorSubject<TenantConfig | null>(
    null,
  );

  /**
   * Emits the TenantConfig of the federation, or null when not federated.
   */
  public federationConfig$: Observable<TenantConfig | null> =
    this.federationConfigSubject.asObservable();

  /**
   * Whether the active club is federated (has a linked federation).
   */
  public isFederated$: Observable<boolean> = this.federationConfig$.pipe(
    map((cfg) => cfg !== null),
  );

  /**
   * Emits the federation tenant ID, or null.
   */
  public federationId$: Observable<string | null> =
    this.tenantContext.federationId$;

  /**
   * Collection paths scoped to the federation tenant.
   * Emits null when not federated.
   */
  public federationCollections$: Observable<ReturnType<
    typeof getCollections
  > | null> = this.federationConfig$.pipe(
    map((cfg) => (cfg ? getCollections(cfg.id) : null)),
    shareReplay(1),
  );

  constructor() {
    // Resolve the active federation ID considering multi-sport clubs:
    // each sport entry in clubSports may have its own federationId.
    // When the user switches sport, the federation context switches automatically.
    combineLatest([
      this.tenantContext.federationId$,
      this.sportContext.activeSport$,
    ])
      .pipe(
        map(([globalFedId, activeSport]) => {
          // For multi-sport clubs: prefer the per-sport federation link
          if (activeSport) {
            const sportFedId =
              this.sportContext.getFederationIdForSport(activeSport);
            if (sportFedId) return sportFedId;
          }
          return globalFedId;
        }),
        distinctUntilChanged(),
        switchMap((fedId) => {
          if (!fedId) return of(null);
          return from(
            getDoc(doc(this.firestore, 'tenants', fedId)).then((snap) =>
              snap.exists() ? (snap.data() as TenantConfig) : null,
            ),
          );
        }),
      )
      .subscribe((config) => this.federationConfigSubject.next(config));
  }

  /**
   * Synchronous accessor — safe after auth resolved.
   * Returns null when not federated.
   */
  get federationConfig(): TenantConfig | null {
    return this.federationConfigSubject.getValue();
  }

  /**
   * Synchronous collection paths for the federation tenant.
   * Returns null when not federated.
   */
  get federationCollections(): ReturnType<typeof getCollections> | null {
    const cfg = this.federationConfig;
    return cfg ? getCollections(cfg.id) : null;
  }

  /**
   * Synchronous federation tenant ID.
   * Returns null when not federated.
   */
  get federationId(): string | null {
    return this.tenantContext.federationId;
  }

  /** Returns true when the active club is linked to a federation. */
  get isFederated(): boolean {
    return this.federationConfig !== null;
  }
}
